import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { apiFetch } from "../api/client.ts";

type Role = "admin" | "dispatcher" | "technician";

interface User {
  id: string;
  email: string;
  full_name: string;
  role: Role;
  phone_e164: string | null;
  preferred_locale: string;
  is_active: boolean;
  totp_enabled: boolean;
  created_at: string;
  last_login: string | null;
}

const listUsers = () => apiFetch<User[]>("/users");
const createUser = (payload: { email: string; full_name: string; role: Role; phone_e164?: string | null; send_invite: boolean }) =>
  apiFetch<User>("/users", { method: "POST", body: JSON.stringify(payload) });
const updateUser = (id: string, patch: Partial<User>) =>
  apiFetch<User>(`/users/${id}`, { method: "PATCH", body: JSON.stringify(patch) });

const BLANK_FORM = { email: "", full_name: "", role: "dispatcher" as Role, phone_e164: "", send_invite: true };

type EditForm = { full_name: string; role: Role; phone_e164: string };

export default function UsersPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["users"], queryFn: listUsers });

  const [form, setForm] = useState(BLANK_FORM);
  const [creating, setCreating] = useState(false);

  const [editing, setEditing] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ full_name: "", role: "technician", phone_e164: "" });

  const create = useMutation({
    mutationFn: () => createUser({ ...form, phone_e164: form.phone_e164 || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setCreating(false);
      setForm(BLANK_FORM);
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => updateUser(id, { is_active: active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const edit = useMutation({
    mutationFn: () =>
      updateUser(editing!.id, {
        full_name: editForm.full_name,
        role: editForm.role,
        phone_e164: editForm.phone_e164 || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setEditing(null);
    },
  });

  function openEdit(user: User) {
    setCreating(false);
    setEditing(user);
    setEditForm({
      full_name: user.full_name,
      role: user.role,
      phone_e164: user.phone_e164 ?? "",
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Users</h1>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setEditing(null);
            setCreating((x) => !x);
          }}
        >
          {creating ? "Cancel" : "Invite user"}
        </button>
      </div>

      {creating && (
        <form
          className="card grid grid-cols-1 gap-3 md:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          {create.error && (
            <div className="md:col-span-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {(create.error as Error).message || "Request failed"}
            </div>
          )}
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Email</span>
            <input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Full name</span>
            <input className="input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Role</span>
            <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
              <option value="admin">Admin</option>
              <option value="dispatcher">Dispatcher</option>
              <option value="technician">Technician</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Phone (E.164)</span>
            <input className="input" value={form.phone_e164} onChange={(e) => setForm({ ...form, phone_e164: e.target.value })} placeholder="+15125551234" />
          </label>
          <div className="md:col-span-2">
            <button type="submit" className="btn" disabled={create.isPending}>Invite</button>
          </div>
        </form>
      )}

      {editing && (
        <form
          className="card grid grid-cols-1 gap-3 md:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            edit.mutate();
          }}
        >
          <div className="md:col-span-2 flex items-center justify-between">
            <h2 className="font-medium">Edit user — {editing.email}</h2>
            <button type="button" className="text-sm text-slate-500 hover:text-slate-700" onClick={() => setEditing(null)}>Cancel</button>
          </div>
          {edit.error && (
            <div className="md:col-span-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {(edit.error as Error).message || "Request failed"}
            </div>
          )}
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Full name</span>
            <input
              className="input"
              value={editForm.full_name}
              onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
              required
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Role</span>
            <select
              className="input"
              value={editForm.role}
              onChange={(e) => setEditForm({ ...editForm, role: e.target.value as Role })}
            >
              <option value="admin">Admin</option>
              <option value="dispatcher">Dispatcher</option>
              <option value="technician">Technician</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Phone (E.164)</span>
            <input
              className="input"
              value={editForm.phone_e164}
              onChange={(e) => setEditForm({ ...editForm, phone_e164: e.target.value })}
              placeholder="+15125551234"
            />
          </label>
          <div className="md:col-span-2">
            <button type="submit" className="btn" disabled={edit.isPending}>Save changes</button>
          </div>
        </form>
      )}

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2">Email</th>
              <th className="hidden px-4 py-2 md:table-cell">Name</th>
              <th className="px-4 py-2">Role</th>
              <th className="hidden px-4 py-2 md:table-cell">2FA</th>
              <th className="hidden px-4 py-2 md:table-cell">Status</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data?.map((u) => (
              <tr key={u.id} className={editing?.id === u.id ? "bg-blue-50" : undefined}>
                <td className="break-all px-4 py-2">{u.email}</td>
                <td className="hidden px-4 py-2 md:table-cell">{u.full_name}</td>
                <td className="px-4 py-2 capitalize">{u.role}</td>
                <td className="hidden px-4 py-2 md:table-cell">
                  {u.totp_enabled ? <span className="text-green-600">✓ on</span> : "off"}
                </td>
                <td className="hidden px-4 py-2 md:table-cell">
                  {u.is_active ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">active</span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">disabled</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right space-x-3 whitespace-nowrap">
                  <button
                    type="button"
                    className="text-brand-600 hover:underline"
                    onClick={() => openEdit(u)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="text-slate-500 hover:underline"
                    onClick={() => toggle.mutate({ id: u.id, active: !u.is_active })}
                  >
                    {u.is_active ? "Disable" : "Enable"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
